<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <strong>TB Chair keyword review</strong>
      <b-button
        v-if="canEdit"
        size="sm"
        variant="outline-secondary"
        :disabled="loading || saving || similarBusy"
        @click="checkSimilarities"
      >
        Check similarities
      </b-button>
    </div>

    <ifac-loader v-if="loading || saving" />

    <div v-show="!(loading || saving)">
      <b-card
        v-for="committee in committeesWithRows"
        :key="committee.id"
        class="mb-3"
      >
        <div class="d-flex justify-content-between align-items-center mb-2">
          <strong>CC{{ committee.number }} — {{ committee.name }}</strong>
        </div>

        <b-table
          small
          responsive
          bordered
          :fields="visibleFields"
          :items="committee.rows"
          class="mb-0"
        >
          <template #cell(sourceTcId)="{ item }">
            TC{{ tcLabel(item.sourceTcId) }}
          </template>

          <template #cell(existingKeywordName)="{ item }">
            {{ item.existingKeywordName || '—' }}
          </template>

          <template #cell(tcProposedName)="{ item }">
            <span :class="warningClass(item.rowId, 'tcProposedName')">
              {{ item.tcProposedName || '—' }}
            </span>
          </template>

          <template #cell(ccProposedName)="{ item }">
            <span :class="warningClass(item.rowId, 'ccProposedName')">
              {{ item.ccProposedName || '—' }}
            </span>
          </template>

          <template #cell(tbProposedName)="{ item }">
            <b-form-input
              v-if="canEdit && !item.tbUsesCcProposal && !isTbRemoved(item)"
              v-model="item.tbProposedName"
              size="sm"
              :class="warningClass(item.rowId, 'tbProposedName')"
              style="min-width: 18rem;"
              @input="onTbEdit(item)"
            />
            <span v-else :class="warningClass(item.rowId, 'tbProposedName')">
              {{ item.tbProposedName || '—' }}
            </span>
          </template>

          <template #cell(targetTcId)="{ item }">
            <b-form-select
              v-if="canEdit && !item.tbUsesCcProposal && !isTbRemoved(item)"
              v-model="item.targetTcId"
              :options="technicalCommitteeOptions"
              size="sm"
              value-field="id"
              text-field="label"
              style="min-width: 8rem;"
              @change="onTbEdit(item)"
            />
            <span v-else>TC{{ tcLabel(item.targetTcId) }}</span>
          </template>

          <template #cell(override)="{ item }">
            <template v-if="canEdit">
              <b-button
                v-if="item.tbUsesCcProposal"
                size="sm"
                variant="link"
                class="px-1"
                @click="changeProposal(item)"
              >
                Change proposal
              </b-button>
              <b-button
                v-else
                size="sm"
                variant="link"
                class="px-1"
                @click="restoreCcProposal(item)"
              >
                Get back to CC proposal
              </b-button>
              <b-button
                v-if="isTbRemoved(item)"
                size="sm"
                variant="link"
                class="px-1"
                @click="restoreKeyword(item)"
              >
                Restore keyword
              </b-button>
              <b-button
                v-if="!isTbRemoved(item)"
                size="sm"
                variant="link"
                class="px-1 text-danger"
                @click="removeKeyword(item)"
              >
                Remove keyword
              </b-button>
            </template>
          </template>

          <template #cell(similar)="{ item }">
            <b-button
              size="sm"
              variant="link"
              class="px-1 text-nowrap"
              :disabled="!proposedNameForSimilarity(item) || similarBusy"
              @click="$emit('see-similar', item)"
            >
              See similar keywords
            </b-button>
          </template>
        </b-table>
      </b-card>

      <b-button
        v-if="canEdit"
        size="sm"
        variant="primary"
        :disabled="loading || saving || similarBusy"
        @click="saveReview"
      >
        Save TB review
      </b-button>
    </div>
  </div>
</template>

<script>
import { IfacLoader } from '@ifac/ui';
import Revisions from '@/services/Api/TechnicalAreas/Revisions';

export default {
  name: 'TbKeywordsReviewTable',
  components: {
    IfacLoader,
  },
  props: {
    revisionId: {
      type: String,
      required: true,
    },
    snapshot: {
      type: Object,
      required: true,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    similarBusy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      saving: false,
      rows: [],
      warnings: {},
      technicalCommittees: [],
      tbOverrides: {},
      fields: [
        { key: 'sourceTcId', label: 'Prev. TC', thStyle: { minWidth: '5.5rem' } },
        { key: 'existingKeywordName', label: 'Existing keyword', thStyle: { minWidth: '12rem' } },
        { key: 'tcProposedName', label: 'Proposed by TC Chair', thStyle: { minWidth: '12rem' } },
        { key: 'ccProposedName', label: 'Proposed by CC Chair', thStyle: { minWidth: '12rem' } },
        { key: 'tbProposedName', label: 'TB proposal', thStyle: { minWidth: '18rem' } },
        { key: 'targetTcId', label: 'Next TC', thStyle: { minWidth: '8rem' } },
        { key: 'override', label: '', thStyle: { minWidth: '12rem' } },
        { key: 'similar', label: '', thStyle: { minWidth: '10rem' } },
      ],
    };
  },
  computed: {
    visibleFields() {
      if (this.canEdit) {
        return this.fields;
      }

      return this.fields.filter((field) => field.key !== 'override');
    },
    committeesWithRows() {
      return (this.snapshot?.coordinatingCommittees || [])
        .filter((committee) => !committee.deleted)
        .map((committee) => ({
          ...committee,
          rows: this.rows.filter((row) => this.rowBelongsToCommittee(row, committee.id)),
        }))
        .filter((committee) => committee.rows.length > 0);
    },
    technicalCommitteeOptions() {
      return this.technicalCommittees.map((tc) => ({
        id: tc.id,
        label: tc.label ? `TC${tc.label}` : tc.name,
      }));
    },
  },
  async created() {
    await this.loadReview();
  },
  methods: {
    normalizeTbOverrides(raw) {
      if (raw == null || Array.isArray(raw)) {
        return {};
      }

      return { ...raw };
    },
    ensureTbOverridesObject() {
      if (Array.isArray(this.tbOverrides) || this.tbOverrides == null) {
        this.tbOverrides = {};
      }
    },
    async loadReview() {
      this.loading = true;

      try {
        const response = await Revisions.getTbKeywordReview(this.revisionId);
        const { data } = response.data;
        this.rows = data.rows || [];
        this.warnings = data.warnings || {};
        this.technicalCommittees = data.technicalCommittees || [];
        this.tbOverrides = this.normalizeTbOverrides(data.tbOverrides);
      } finally {
        this.loading = false;
      }
    },
    rowBelongsToCommittee(row, coordinatingCommitteeId) {
      const sourceTc = this.technicalCommittees.find(
        (tc) => Number(tc.id) === Number(row.sourceTcId),
      );
      const targetTc = this.technicalCommittees.find(
        (tc) => Number(tc.id) === Number(row.targetTcId),
      );

      return Number(sourceTc?.coordinatingCommitteeId) === Number(coordinatingCommitteeId)
        || Number(targetTc?.coordinatingCommitteeId) === Number(coordinatingCommitteeId);
    },
    tcLabel(technicalCommitteeId) {
      const match = this.technicalCommittees.find(
        (tc) => Number(tc.id) === Number(technicalCommitteeId),
      );

      return match?.label || technicalCommitteeId;
    },
    isTbRemoved(row) {
      return !row.tbUsesCcProposal && !String(row.tbProposedName || '').trim();
    },
    seededTbProposedName(row) {
      return row.ccProposedName || row.tcProposedName || row.existingKeywordName || '';
    },
    changeProposal(row) {
      this.$set(row, 'tbUsesCcProposal', false);
      this.$set(row, 'tbProposedName', this.seededTbProposedName(row));
      this.onTbEdit(row);
    },
    restoreCcProposal(row) {
      this.ensureTbOverridesObject();
      this.$set(row, 'tbUsesCcProposal', true);
      this.$set(row, 'tbProposedName', row.ccProposedName || row.tcProposedName || '');
      this.$set(row, 'targetTcId', row.targetTcId || row.sourceTcId);
      this.$delete(this.tbOverrides, row.rowId);
    },
    restoreKeyword(row) {
      this.$set(row, 'tbUsesCcProposal', false);
      this.$set(row, 'tbProposedName', this.seededTbProposedName(row));
      this.$set(row, 'targetTcId', row.targetTcId || row.sourceTcId);
      this.onTbEdit(row);
    },
    removeKeyword(row) {
      this.$set(row, 'tbUsesCcProposal', false);
      this.$set(row, 'tbProposedName', '');
      this.onTbEdit(row);
    },
    onTbEdit(row) {
      this.ensureTbOverridesObject();
      this.$set(this.tbOverrides, row.rowId, {
        useCcProposal: false,
        tbProposedName: row.tbProposedName,
        targetTcId: row.targetTcId,
      });
    },
    proposedNameForSimilarity(row) {
      if (this.isTbRemoved(row)) {
        return '';
      }

      return row.tbProposedName || row.ccProposedName || row.tcProposedName;
    },
    warningClass(rowId, column) {
      return Revisions.warningClass(this.warnings, rowId, column);
    },
    countWarningsByType(type) {
      return ['tcProposedName', 'ccProposedName', 'tbProposedName'].reduce((total, column) => {
        const columnWarnings = this.warnings?.[column] || {};

        return total + Object.values(columnWarnings).filter((warning) => warning === type).length;
      }, 0);
    },
    async checkSimilarities() {
      const response = await Revisions.show(this.revisionId);
      this.$emit('refresh-revision', response.data.data);
      await this.loadReview();

      const exactCount = this.countWarningsByType('exact_duplicate');
      const similarCount = this.countWarningsByType('similar_match');

      if (exactCount === 0 && similarCount === 0) {
        this.$snack.show({
          text: 'No exact or similar keyword warnings in the review columns.',
        });

        return;
      }

      this.$snack.show({
        text: `${exactCount} exact duplicate and ${similarCount} similar keyword warnings in the review columns.`,
      });
    },
    /**
     * Persist TB overrides into the revision snapshot.
     * @param {{ quiet?: boolean }} [options] - quiet: skip success snack (e.g. auto-save before validation)
     * @returns {Promise<object|null>} Updated revision, or null on failure
     */
    async saveReview({ quiet = false } = {}) {
      if (!this.canEdit) {
        return null;
      }

      this.saving = true;
      await this.$nextTick();

      try {
        const response = await Revisions.updateTbKeywordReview(
          this.revisionId,
          this.rows,
          this.normalizeTbOverrides(this.tbOverrides),
        );
        const revision = response.data.data;
        this.$emit('saved', revision);
        await this.loadReview();

        if (!quiet) {
          this.$snack.success({ text: 'TB review saved.' });
        }

        return revision;
      } catch (error) {
        this.$snack.danger({
          text: error.response?.data?.error?.message || 'Could not save TB review.',
        });

        return null;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
